import {
  type AnimationState,
  isRestState,
  type MarkStyle,
  type ParagraphFormat,
  type SceneElement,
  type Shadow,
  type TextElement,
  textEffectCss,
} from "@cva/shared";
import type { CSSProperties } from "react";

/**
 * Style builders. The editor uses the same functions for its inline text
 * editor so the text does not jump when switching into edit mode.
 */

export const FALLBACK_FONTS = `"Inter", system-ui, sans-serif`;

export function fontFamilyCss(family: string): string {
  return `"${family.replace(/"/g, "")}", ${FALLBACK_FONTS}`;
}

/**
 * Position, size, rotation and opacity of an element. With an animation state
 * (video), its offset, scale, rotation, opacity and blur are added.
 */
export function boxStyle(element: SceneElement, state?: AnimationState | null): CSSProperties {
  const animated = state && !isRestState(state) ? state : null;
  const opacity = element.opacity * (animated?.opacity ?? 1);
  const transforms: string[] = [];
  if (animated && (animated.x || animated.y))
    transforms.push(`translate(${animated.x}px, ${animated.y}px)`);
  const rotation = element.rotation + (animated?.rotate ?? 0);
  if (rotation) transforms.push(`rotate(${rotation}deg)`);
  if (animated && animated.scale !== 1) transforms.push(`scale(${Math.max(0, animated.scale)})`);
  return {
    position: "absolute",
    left: element.x,
    top: element.y,
    width: element.width,
    height: element.height,
    opacity: opacity === 1 ? undefined : opacity,
    transform: transforms.length > 0 ? transforms.join(" ") : undefined,
    transformOrigin: "center",
    boxSizing: "border-box",
    filter: animated?.blur ? `blur(${animated.blur}px)` : undefined,
  };
}

/** Combines CSS filter functions (animation blur + drop shadow). */
export function joinFilters(...filters: (string | undefined)[]): string | undefined {
  const list = filters.filter(Boolean);
  return list.length > 0 ? list.join(" ") : undefined;
}

export function boxShadowCss(shadow: Shadow | undefined): string | undefined {
  return shadow ? `${shadow.x}px ${shadow.y}px ${shadow.blur}px ${shadow.color}` : undefined;
}

export function dropShadowCss(shadow: Shadow | undefined): string | undefined {
  return shadow
    ? `drop-shadow(${shadow.x}px ${shadow.y}px ${shadow.blur / 2}px ${shadow.color})`
    : undefined;
}

/** Outer box of a text element (positions the content vertically). */
export function textBoxStyle(element: TextElement, state?: AnimationState | null): CSSProperties {
  const justify =
    element.style.verticalAlign === "middle"
      ? "center"
      : element.style.verticalAlign === "bottom"
        ? "flex-end"
        : "flex-start";
  return {
    ...boxStyle(element, state),
    display: "flex",
    flexDirection: "column",
    justifyContent: justify,
  };
}

/** Typography of a text element's content. */
export function textContentStyle(element: TextElement): CSSProperties {
  const { style } = element;
  return {
    fontFamily: fontFamilyCss(style.fontFamily),
    fontSize: style.fontSize,
    fontWeight: style.fontWeight,
    fontStyle: style.italic ? "italic" : "normal",
    fontSynthesis: "none",
    color: style.color,
    textAlign: style.align,
    lineHeight: style.lineHeight,
    letterSpacing: style.letterSpacing ? `${style.letterSpacing}em` : undefined,
    textTransform: style.uppercase ? "uppercase" : undefined,
    ...decorationStyle(style.underline, style.strike),
    whiteSpace: "pre-wrap",
    overflowWrap: "break-word",
    wordBreak: "normal",
    textRendering: "geometricPrecision",
    WebkitTextStroke:
      style.stroke && style.stroke.width > 0
        ? `${style.stroke.width * 2}px ${style.stroke.color}`
        : undefined,
    paintOrder: style.stroke ? "stroke fill" : undefined,
    textShadow: element.shadow
      ? `${element.shadow.x}px ${element.shadow.y}px ${element.shadow.blur}px ${element.shadow.color}`
      : undefined,
    // Text effects (none: nothing added, the output stays the same).
    ...(textEffectCss(element) as CSSProperties),
    margin: 0,
    padding: 0,
    minHeight: "1lh",
    // Never squeezed to the box: auto height measures the content.
    flexShrink: 0,
  };
}

function decorationStyle(
  underline: boolean | undefined,
  strike: boolean | undefined,
): CSSProperties {
  if (!underline && !strike) return {};
  return {
    textDecorationLine:
      underline && strike ? "underline line-through" : underline ? "underline" : "line-through",
    textDecorationThickness: "0.06em",
    textUnderlineOffset: underline ? "0.12em" : undefined,
  };
}

/**
 * Formatting of a run of mixed text (scene/richtext.ts) on top of the
 * element's typography. Underline and line-through only add to it.
 */
export function textRunStyle(style: MarkStyle, element: TextElement): CSSProperties {
  const out: CSSProperties = {};
  if (style.fontFamily) out.fontFamily = fontFamilyCss(style.fontFamily);
  if (style.fontSize !== undefined) {
    out.fontSize = style.fontSize;
    // Letter spacing is in em of the run's own size.
    if (element.style.letterSpacing) out.letterSpacing = `${element.style.letterSpacing}em`;
  }
  if (style.fontWeight !== undefined) out.fontWeight = style.fontWeight;
  if (style.italic !== undefined) out.fontStyle = style.italic ? "italic" : "normal";
  if (style.color) out.color = style.color;
  Object.assign(
    out,
    decorationStyle(
      style.underline && !element.style.underline,
      style.strike && !element.style.strike,
    ),
  );
  return out;
}

/** Indent of one list level, in em of the element's font size. */
export const LIST_STEP_EM = 1.4;

/** Paragraph of mixed text: indent and the room for its list marker. */
export function paragraphStyle(format: ParagraphFormat | null | undefined): CSSProperties {
  const start = ((format?.indent ?? 0) + (format?.list ? 1 : 0)) * LIST_STEP_EM;
  if (start === 0) return {};
  return {
    position: "relative",
    paddingLeft: `${start}em`,
    ...(format?.list ? ({ "--list-marker-end": `${start - 0.35}em` } as CSSProperties) : {}),
  };
}

/**
 * List markers are drawn by this rule from the paragraph's `data-list-marker`,
 * so they are not part of the text (the editor cannot place the caret in them).
 */
export const LIST_MARKER_CSS =
  "[data-list-marker]::before{content:attr(data-list-marker);position:absolute;top:0;right:calc(100% - var(--list-marker-end));white-space:nowrap;text-decoration:none}";
